/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin-users.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { useTranslation, useBoolean } from 'hooks';
import { systemUsers_OpraQueries_systemUsers_User, exportUsers as ExportUsers } from 'queries/generated/uam.generated';
import { GET_SYSTEM_USERS, EXPORT_USERS } from 'queries/uam';
import Constants from 'utils/constants';
import { downloadUTF8File } from 'utils/file-service';
import NotificationService from 'utils/notification';
import ResetPasswordModal from 'views/components/admin/reset-password-modal';
import EditSystemUser from 'views/components/admin/users/edit-system-user';
import SystemUsersGrid from 'views/components/admin/users/system-users-grid';

import Button from 'views/components/core/buttons/button';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

const AdminUsers = ({ user }: any) => {
    const { t } = useTranslation();
    const { data, error, loading } = useQuery(GET_SYSTEM_USERS);

    const [exportUsers, { loading: exportLoading, error: exportError }] = useLazyQuery<ExportUsers>(EXPORT_USERS);

    const [currentUser, setCurrentUser] = useState(null);

    const canEdit = user?.canWriteSystemAdminLevel();

    const openAddUser = useBoolean(false);
    const openResetPassword = useBoolean(false);

    if (loading) return <Loading />;

    if (error) return <Error error={error} />;

    const members = data?.systemUsers ?? [];

    const isEnabled = (item: systemUsers_OpraQueries_systemUsers_User) => item.isEnabled;

    return (
        <>
            <EditSystemUser
                open={openAddUser.value}
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else if (action === 'resent') NotificationService.showNotification(t('uam:inviteResentSuccessful'));
                    else {
                        if (action === 'success')
                            NotificationService.showNotification(t('uam:newUserSuccessfullyCreated'));
                        if (action === 'edit')
                            NotificationService.showNotification(
                                t('uam:userSuccessfullyUpdated', { id: user?.recordId }),
                            );
                        openAddUser.setFalse();
                        setCurrentUser(null);
                    }
                }}
                user={currentUser}
                onExistEmail={() => {
                    NotificationService.showNotification(t('uam:emailAlreadyInUseSysTRI'));
                }}
                systemUsers={members}
                loggedUser={user}
            />
            <ResetPasswordModal
                open={openResetPassword.value}
                orgCode=""
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success')
                            NotificationService.showNotification(t('login:passwordResetEmailSent'));
                        openResetPassword.setFalse();
                        setCurrentUser(null);
                    }
                }}
                email={currentUser?.user ? currentUser.user.email : currentUser?.email}
            />
            <PageTitle text={t('uam:manageUsers')} />
            <br />
            <br />
            {exportError && <Error error={exportError} />}
            {exportLoading && <Loading />}
            {user?.systemAdminLevel >= 4 && !exportLoading && (
                <Button
                    id="export-users"
                    style={{ display: 'block', marginLeft: 'auto', marginBottom: 10 }}
                    className="export-all-users main-button"
                    disabled={user?.systemAdminLevel < 4}
                    onClick={async () => {
                        // Export
                        const response = await exportUsers({
                            variables: { organisationKey: Constants.ObjectId.Empty },
                        });

                        if (response?.data?.exportUsers) {
                            // The Byte Order Mark is required, otherwise all the special characters are displayed incorrectly
                            const BOM = '\uFEFF';
                            downloadUTF8File(
                                'OPRA-AllUsers.csv',
                                encodeURIComponent(`${BOM}${response.data.exportUsers}`),
                            );
                        }
                    }}
                >
                    Export All OPRA Users
                </Button>
            )}
            <Button
                id="add-users"
                style={{ display: 'flex', marginLeft: 'auto', marginBottom: 30 }}
                className="add-cv-btn main-button"
                disabled={!canEdit}
                onClick={() => {
                    openAddUser.setTrue();
                }}
            >
                {t('uam:addUser')}
            </Button>
            <SystemUsersGrid
                items={members ?? []}
                isSystem
                actions={(item: systemUsers_OpraQueries_systemUsers_User) => [
                    {
                        label: 'uam:editUser',
                        icon: ['fal', 'edit'],
                        onClick: () => {
                            setCurrentUser(item);
                            openAddUser.setTrue();
                        },
                        enabled: canEdit,
                    },
                    {
                        label: 'login:resetPassword',
                        icon: ['far', 'redo-alt'],
                        onClick: () => {
                            setCurrentUser(item);
                            openResetPassword.setTrue();
                        },
                        enabled: isEnabled(item) && canEdit,
                    },
                ]}
            />
        </>
    );
};

export default AdminUsers;
